// ===================================================================================================
// Filename: src/app/app.routes.ts
// VERSION: v3.0.0 - RBAC Capability-Guard Integration
// DATE:    2026-05-06
// AUTHOR:  Florian / Claude
//
// ===================================================================================================
// WAS IST NEU IN v3.0.0 (gegenüber v2.0.0)
// ===================================================================================================
//
// 1) NEUER IMPORT: projectAccessGuard
//    ────────────────────────────────
//    Aus dem RBAC-Phase-2-Paket. Dieser Guard läuft NACH dem calStatusGuard
//    und prüft ob der User die nötige Capability für die jeweilige Page hat.
//
//    Capability-Definitionen liegen in:
//      src/app/config/page-access.config.ts (welche Page braucht welche Capability)
//      src/app/core/auth/role-capabilities.map.ts (welche Rolle hat welche Capabilities)
//
//
// 2) GUARD-CHAINING: [calStatusGuard, projectAccessGuard]
//    ────────────────────────────────────────────────────
//    Bei JEDER geschützten Route wurde der einzelne calStatusGuard ergänzt um
//    den projectAccessGuard.
//
//    Reihenfolge ist WICHTIG und bewusst so:
//      1. calStatusGuard läuft ZUERST → prüft Session + CAL,
//         lädt nebenbei den Permission-Cache (CalStatusService).
//      2. projectAccessGuard läuft DANACH → liest aus dem bereits geladenen
//         Cache, KEIN zusätzlicher API-Call. Prüft ob der User die nötige
//         Capability für die jeweilige Page hat.
//
//    Wenn der Guard blockt:
//      → Redirect zu /insufficient-permission mit Kontext-Querystring
//      → Page erklärt freundlich was fehlt und wer helfen kann
//
//
// 3) NEUE PUBLIC ROUTE: /insufficient-permission
//    ───────────────────────────────────────────
//    Wird vom projectAccessGuard angesteuert wenn ein Zugriff verweigert wird.
//    MUSS public sein (keine Guards), sonst → Endlos-Redirect-Schleife.
//
//
// 4) BUGFIX: app-management/bulk-user-provisioning bekommt jetzt Guards
//    ─────────────────────────────────────────────────────────────────
//    In v2.0.0 war diese Route OHNE jegliche Guards. Das war ein Bug:
//    jeder unauthentifizierte User konnte die Page-Komponente laden
//    (auch wenn das Backend die API-Calls blockiert hätte).
//    v3.0.0 fügt [calStatusGuard, projectAccessGuard] hinzu — die
//    Page wird nur für User mit USER_PROVISION Capability laden
//    (org-perm >= 5, also Admin oder Super-Admin).
//
//
// 5) WAS BLEIBT UNVERÄNDERT
//    ───────────────────────
//    - Alle PUBLIC Routes bleiben public (keine Guards)
//    - Alle Pfade, Component-Imports, Lazy-Loading-Strukturen bleiben gleich
//    - /info-target-mapper bleibt OHNE Guard (Anti-Redirect-Loop, siehe Kommentar dort)
//    - /no-license bleibt OHNE Guard (User muss hier hinkönnen wenn keine CAL)
//    - /self-assign bleibt OHNE Guard (Tryout-Spezialfall)
//    - /login und /app-management/login bleiben OHNE Guards
//
//
// ===================================================================================================
// ANATOMIE EINER GESCHÜTZTEN ROUTE (Beispiel)
// ===================================================================================================
//
//   {
//     path: 'project/:pslot/sprint/new/edit',
//     canActivate: [calStatusGuard, projectAccessGuard],
//     // ↑ ZWEI Guards in Kette:
//     //   1. calStatusGuard:   Session + CAL OK?                    → sonst /login oder /no-license
//     //   2. projectAccessGuard: hat User SPRINT_CREATE Capability? → sonst /insufficient-permission
//     //
//     //   Welche Capability nötig ist, steht in page-access.config.ts.
//     //   Welche Rolle diese Capability hat, steht in role-capabilities.map.ts.
//
//     loadComponent: () =>
//       import('./pages/sprint-editor/sprint-editor-page.component')
//         .then(m => m.SprintEditorPageComponent)
//   },
//
//
// ===================================================================================================
// DEPLOYMENT-REIHENFOLGE (sehr wichtig!)
// ===================================================================================================
//
//   Diese Datei NICHT alleine deployen!
//   Voraussetzungen müssen ZUERST im Code sein:
//
//     ✅ src/app/core/auth/capabilities.enum.ts
//     ✅ src/app/core/auth/roles.enum.ts
//     ✅ src/app/core/auth/role-capabilities.map.ts
//     ✅ src/app/core/auth/permission-role.map.ts
//     ✅ src/app/services/permission.service.ts
//     ✅ src/app/services/capability.service.ts
//     ✅ src/app/directives/has-capability.directive.ts
//     ✅ src/app/config/page-access.config.ts
//     ✅ src/app/guards/project-access.guard.ts
//     ✅ src/app/pages/insufficient-permission/insufficient-permission-page.component.{ts,html,scss}
//
//   Sonst Build-Fehler: "Cannot find module './guards/project-access.guard'".
//
//
// ===================================================================================================
// HISTORIE
// ===================================================================================================
//
// v3.0.0 (2026-05-06): RBAC Capability-Guard Integration
//   + projectAccessGuard auf alle geschützten Routes
//   + /insufficient-permission als public Route
//   + Bugfix: bulk-user-provisioning bekommt Guards
//
// v2.0.0 (2025-01-24): CAL Route Guard Integration (calStatusGuard)
//
// v1.0.0 (2025-12-22): Initial Routing mit Sprint Editor (S/SEEP Pattern)
//
// ===================================================================================================

import { Routes } from '@angular/router';

// ── Layer 1: Session + CAL Guard (vorhanden seit v2.0.0) ──────────────────────────────────────────
// Prüft: Cookie ceeq_token vorhanden? Redis-Session gültig? CAL aktiv?
// Bei Verstoß: Redirect → /login (Session) oder /no-license (CAL)
import { calStatusGuard } from './guards/cal-status.guard';

// ── Layer 2: Capability-basierter Page-Access Guard (NEU in v3.0.0) ───────────────────────────────
// Prüft: hat der User die für diese Page nötige Capability?
// Capabilities sind definiert in src/app/config/page-access.config.ts
// Bei Verstoß: Redirect → /insufficient-permission (mit Kontext-Querystring)
import { projectAccessGuard } from './guards/project-access.guard';


export const routes: Routes = [

  // ═══════════════════════════════════════════════════════════════════════════
  // POSTEINGANG — „Für dich" (2do-Queue & Coordination Bus, Item #79)
  // ═══════════════════════════════════════════════════════════════════════════
  //
  // NUR calStatusGuard, BEWUSST OHNE projectAccessGuard:
  //   Der Posteingang ist personenbezogen, nicht projektbezogen. Er zeigt, was
  //   DIESEN Menschen erreicht hat — über alle Projekte hinweg. Ein
  //   Projekt-Guard hätte hier keinen Bezugspunkt: es gibt keinen pslot, gegen
  //   den er prüfen könnte.
  //
  //   Die Abschottung leistet das BACKEND: jede Abfrage in messages.route.js
  //   filtert auf `target_uuid = req.user.uuid`. Es gibt keinen Parameter, der
  //   das überschreiben könnte, und keinen Endpunkt für fremde Posteingänge.
  //   Nachgeprüft am 2026-08-08: der Agent sah 0 Nachrichten, während das
  //   Projekt-Brett 2 zeigte.
  {
    path: 'messages',
    canActivate: [calStatusGuard],
    loadComponent: () =>
      import('./pages/messages/messages.component')
        .then(m => m.MessagesComponent)
  },

  // ── Team-Brett: ALLES, was in diesem Projekt geschehen ist ───────────────
  //   Liest GET /api/board (Ereignisse, nicht Nachrichten — seit 2026-08-19,
  //   Item #95 A-1). Zuvor las es die Nachrichten-Queue und zeigte damit
  //   sieben Prozent des Geschehens.
  //   ceeq.it: Item #95, Task #421 (zuvor Item #81, Task #290)
  {
    path: 'project/:pslot/board',
    canActivate: [calStatusGuard],
    loadComponent: () =>
      import('./pages/message-board/message-board.component')
        .then(m => m.MessageBoardComponent)
  },

  // ── Meine Nachrichten: Eingang UND Ausgang ───────────────────────────────
  //   Projektgebunden, damit der Ausgang auf ein Projekt eingeschränkt werden
  //   kann. Der Zweck ist das Nachfassen: „Donnerstag gefragt, Montag sehen ob
  //   geantwortet wurde."
  //   ceeq.it: Item #95, Task #421
  {
    path: 'project/:pslot/mailbox',
    canActivate: [calStatusGuard],
    loadComponent: () =>
      import('./pages/mailbox/mailbox.component')
        .then(m => m.MailboxComponent)
  },

  // ── Interessens-Steuerung: wovon will ich in diesem Projekt hören? ────────
  //   Projektgebunden, weil die PSP-Hauptlinien zu einem Projekt gehören.
  //   Der Posteingang darüber ist es nicht — er führt über alle Projekte.
  //   ceeq.it: Item #84 in _34, Task #340
  {
    path: 'project/:pslot/message-interests',
    canActivate: [calStatusGuard],
    loadComponent: () =>
      import('./pages/message-interests/message-interests.component')
        .then(m => m.MessageInterestsComponent)
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC ROUTES (keine Guards — müssen immer erreichbar sein)
  // ═══════════════════════════════════════════════════════════════════════════

  // ── Default-Redirect: leere URL → /login ─────────────────────────────────
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full'
  },

  // ── Login-Seite ──────────────────────────────────────────────────────────
  // Public: Login muss immer erreichbar sein, sonst kann sich niemand einloggen.
  {
    path: 'login',
    loadComponent: () =>
      import('./pages/login/login-page.component')
        .then(m => m.LoginPageComponent)
    // ✅ NO GUARD - Login is always accessible
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ✨ NEU IN v3.0.0: Insufficient-Permission-Page
  // ─────────────────────────────────────────────────────────────────────────
  // Wird vom projectAccessGuard angesteuert wenn ein User auf eine Page
  // navigieren will für die ihm die Capability fehlt.
  //
  // MUSS public sein:
  //   Diese Page erklärt dem User WARUM eine andere Page nicht erreichbar ist.
  //   Hätte sie selbst Guards, würde der Guard ggf. wieder hierher redirecten
  //   → Endlos-Schleife. Also: keine Guards.
  //
  // Die Page liest ihre Information aus dem URL-Querystring (?from=, ?missingCap=,
  // ?pslot=, ...) — gesetzt vom projectAccessGuard.
  // ─────────────────────────────────────────────────────────────────────────
  {
    path: 'insufficient-permission',
    loadComponent: () =>
      import('./pages/insufficient-permission/insufficient-permission-page.component')
        .then(m => m.InsufficientPermissionPageComponent)
    // ✅ NO GUARD - Anti-Redirect-Loop, siehe Kommentar oben
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PROTECTED ROUTES (Layer 1 + Layer 2 — Session/CAL UND Capability-Check)
  // ═══════════════════════════════════════════════════════════════════════════

  // ── Account-Verwaltung (org-scope, OWN_PROFILE_VIEW) ─────────────────────
  {
    path: 'manage-account',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard
    loadComponent: () =>
      import('./pages/manage-account/manage-account-page.component')
        .then(m => m.ManageAccountPageComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PROJECT ENTRY & DASHBOARD
  // ─────────────────────────────────────────────────────────────────────────

  // ── Projekt-Auswahl (org-scope, PROJECT_LIST_VIEW) ───────────────────────
  // Diese Page MUSS für jeden eingeloggten User mit CAL erreichbar sein,
  // sonst kann der User nie ein Projekt betreten.
  {
    path: 'project-select',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard
    loadComponent: () =>
      import('./pages/project-select/project-select-page.component')
        .then(m => m.ProjectSelectPageComponent)
  },

  // ── Project Dashboard (project-scope, DASHBOARD_VIEW) ────────────────────
  // Ein User kommt auf das Dashboard wenn er mindestens USER-Permission im
  // Projekt hat (perm >= 1). Pending Users (perm=0) werden vom Guard zur
  // /insufficient-permission Page geschickt.
  {
    path: 'project/:pslot/dashboard',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard
    loadComponent: () =>
      import('./pages/project-dashboard/project-dashboard-page.component')
        .then(m => m.ProjectDashboardPageComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ITEM EDITOR (Canonical S/SEEP)
  // Capability-Map:
  //   /item/new/edit       → ITEM_CREATE   (Manager+ / PO)
  //   /item/:id/edit       → ITEM_EDIT_OWN (jeder Member, Backend prüft Ownership)
  //   /item/:id            → ITEM_VIEW     (jeder Member)
  //   /items/view          → ITEM_VIEW     (jeder Member)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Item-Creator: braucht ITEM_CREATE ────────────────────────────────────
  {
    path: 'project/:pslot/item/new/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für ITEM_CREATE)
    loadComponent: () =>
      import('./pages/item-editor/item-editor-page.component')
        .then(m => m.ItemEditorPageComponent)
  },

  // ── Item-Editor (existing): braucht ITEM_EDIT_OWN ────────────────────────
  {
    path: 'project/:pslot/item/:item_id/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für ITEM_EDIT_OWN)
    loadComponent: () =>
      import('./pages/item-editor/item-editor-page.component')
        .then(m => m.ItemEditorPageComponent)
  },

  // ── Item-View (Detail): braucht ITEM_VIEW ────────────────────────────────
  {
    path: 'project/:pslot/item/:item_id',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für ITEM_VIEW)
    loadComponent: () =>
      import('./pages/item-view/item-view-page.component')
        .then(m => m.ItemViewPageComponent)
  },

  // ── Items-View (Liste): braucht ITEM_VIEW ────────────────────────────────
  {
    path: 'project/:pslot/items/view',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für ITEM_VIEW)
    loadComponent: () =>
      import('./pages/items-view/items-view-page.component')
        .then(m => m.ItemsViewPageComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // TASK EDITOR (Canonical S/SEEP)
  // Capability-Map:
  //   /task/new/edit  → TASK_CREATE   (jeder Member)
  //   /task/:id/edit  → TASK_EDIT_OWN (jeder Member, Backend prüft Ownership)
  //   /tasks          → TASK_SEEK     (jeder Member, Cross-project search)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Task-Creator: braucht TASK_CREATE ────────────────────────────────────
  {
    path: 'project/:pslot/task/new/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für TASK_CREATE)
    loadComponent: () =>
      import('./pages/task-editor/task-editor-page.component')
        .then(m => m.TaskEditorPageComponent)
  },

  // ── Task-Editor: braucht TASK_EDIT_OWN ───────────────────────────────────
  {
    path: 'project/:pslot/task/:task_id/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für TASK_EDIT_OWN)
    loadComponent: () =>
      import('./pages/task-editor/task-editor-page.component')
        .then(m => m.TaskEditorPageComponent)
  },

  // ── Task Seeker (Cross-project search): braucht TASK_SEEK ────────────────
  // Hinweis: das Code-Format dieser Route hier ist absichtlich kompakt
  // (eine Zeile) — 1:1 wie es im Original stand, damit Diff klein bleibt.
  {
    path: 'project/:pslot/tasks',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für TASK_SEEK)
    loadComponent: () =>
      import('./pages/tasks-mgmt/tasks-mgmt-page.component')
        .then(m => m.TasksMgmtPageComponent)
  },

  // ── Item Seeker: braucht ITEM_SEEK ────────────────────────────────────────
  {
    path: 'project/:pslot/item-seeker',
    canActivate: [calStatusGuard, projectAccessGuard],
    loadComponent: () =>
      import('./pages/item-seeker/item-seeker-page.component')
        .then(m => m.ItemSeekerPageComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SPRINT EDITOR (Canonical S/SEEP)
  // Capability-Map:
  //   /sprint/new/edit  → SPRINT_CREATE (Manager+ / SM)
  //   /sprint/:id/edit  → SPRINT_EDIT   (Manager+ / SM)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Sprint-Creator: braucht SPRINT_CREATE ────────────────────────────────
  {
    path: 'project/:pslot/sprint/new/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für SPRINT_CREATE)
    loadComponent: () =>
      import('./pages/sprint-editor/sprint-editor-page.component')
        .then(m => m.SprintEditorPageComponent)
  },

  // ── Sprint-Editor: braucht SPRINT_EDIT ───────────────────────────────────
  {
    path: 'project/:pslot/sprint/:sp_id/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für SPRINT_EDIT)
    loadComponent: () =>
      import('./pages/sprint-editor/sprint-editor-page.component')
        .then(m => m.SprintEditorPageComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MILESTONE EDITOR (Canonical S/SEEP)
  // Capability-Map:
  //   /milestone/new/edit  → MILESTONE_CREATE (Manager+ / PO)
  //   /milestone/:id/edit  → MILESTONE_EDIT   (Manager+ / PO)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Milestone-Creator: braucht MILESTONE_CREATE ──────────────────────────
  {
    path: 'project/:pslot/milestone/new/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für MILESTONE_CREATE)
    loadComponent: () =>
      import('./pages/milestone-editor/milestone-editor-page.component')
        .then(m => m.MilestoneEditorPageComponent)
  },

  // ── Milestone-Editor: braucht MILESTONE_EDIT ─────────────────────────────
  {
    path: 'project/:pslot/milestone/:ms_id/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für MILESTONE_EDIT)
    loadComponent: () =>
      import('./pages/milestone-editor/milestone-editor-page.component')
        .then(m => m.MilestoneEditorPageComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SOLUTION EDITOR (Canonical S/SEEP, SAFe)
  // Capability-Map:
  //   /solution/new/edit  → SOLUTION_CREATE (Manager+ / Solution Engineer)
  //   /solution/:id/edit  → SOLUTION_EDIT   (Manager+ / Solution Engineer)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Solution-Creator: braucht SOLUTION_CREATE ────────────────────────────
  {
    path: 'project/:pslot/solution/new/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für SOLUTION_CREATE)
    loadComponent: () =>
      import('./pages/solution-editor/solution-editor-page.component')
        .then(m => m.SolutionEditorPageComponent)
  },

  // ── Solution-Editor: braucht SOLUTION_EDIT ───────────────────────────────
  {
    path: 'project/:pslot/solution/:solution_id/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für SOLUTION_EDIT)
    loadComponent: () =>
      import('./pages/solution-editor/solution-editor-page.component')
        .then(m => m.SolutionEditorPageComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SPRINTS VIEW & DAILY
  // Capability-Map:
  //   /sprints/view*               → SPRINT_VIEW (jeder Member)
  //   /feature/dailyteamview/...   → DAILY_VIEW  (jeder Member)
  //   /daily-scrum-matrix*         → DAILY_VIEW  (jeder Member)
  //   /daily-scrum-matrix/.../edit → DAILY_OWN_EDIT (jeder Member, Backend prüft)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Sprints-View (default): braucht SPRINT_VIEW ──────────────────────────
  {
    path: 'project/:pslot/sprints/view',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für SPRINT_VIEW)
    loadComponent: () =>
      import('./pages/sprints-view/sprints-view-page.component')
        .then(m => m.SprintsViewPageComponent)
  },

  // ── Sprint-View (Detail, Lese-Seite eines Sprints): braucht SPRINT_VIEW ──
  // 2026-10-06 (PIA als HUGO, _40 Item #203 Task #694). Analog item/:item_id.
  {
    path: 'project/:pslot/sprint/:sp_id',
    canActivate: [calStatusGuard, projectAccessGuard],
    loadComponent: () =>
      import('./pages/sprint-view/sprint-view-page.component')
        .then(m => m.SprintViewPageComponent)
  },

  // ── Requirement engine (SOPHIST sentence templates): library + vocabulary ──
  // 2026-10-07 (PIA als HUGO, _54 Item #212 Tasks #720/#721). Lesen ab Projektrecht 1,
  // Schreiben prüft das Backend (≥ 3) und die Seite blendet per ITEM_CREATE ein.
  {
    path: 'project/:pslot/requirements',
    canActivate: [calStatusGuard, projectAccessGuard],
    loadComponent: () =>
      import('./pages/requirement-library/requirement-library-page.component')
        .then(m => m.RequirementLibraryPageComponent)
  },
  {
    path: 'project/:pslot/requirements/vocabulary',
    canActivate: [calStatusGuard, projectAccessGuard],
    loadComponent: () =>
      import('./pages/req-vocabulary-admin/req-vocabulary-admin-page.component')
        .then(m => m.ReqVocabularyAdminPageComponent)
  },

  // ── Sprints-View by date: braucht SPRINT_VIEW ────────────────────────────
  {
    path: 'project/:pslot/sprints/view/date/:date',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für SPRINT_VIEW)
    loadComponent: () =>
      import('./pages/sprints-view/sprints-view-page.component')
        .then(m => m.SprintsViewPageComponent)
  },

  // ── Sprints-View by ceeq_spid (prev/next nav): braucht SPRINT_VIEW ───────
  {
    path: 'project/:pslot/sprints/view/ceeq_spid/:ceeq_spid',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für SPRINT_VIEW)
    loadComponent: () =>
      import('./pages/sprints-view/sprints-view-page.component')
        .then(m => m.SprintsViewPageComponent)
  },

  // ── Daily Team View: braucht DAILY_VIEW ──────────────────────────────────
  {
    path: 'project/:pslot/feature/dailyteamview/date/:date',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für DAILY_VIEW)
    loadComponent: () =>
      import('./pages/dailytable-team/dailytable-team-page.component')
        .then(m => m.DailytableTeamPageComponent)
  },

  // ── Daily Scrum Matrix (DSM) Übersicht: braucht DAILY_VIEW ───────────────
  {
    path: 'project/:pslot/daily-scrum-matrix',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für DAILY_VIEW)
    loadComponent: () =>
      import('./pages/daily-scrum-matrix/daily-scrum-matrix.page')
        .then(m => m.DailyScrumMatrixPage)
  },

  // ── DSM by Sprint: braucht DAILY_VIEW ────────────────────────────────────
  {
    path: 'project/:pslot/daily-scrum-matrix/sprint/:sp_id',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für DAILY_VIEW)
    loadComponent: () =>
      import('./pages/daily-scrum-matrix/daily-scrum-matrix.page')
        .then(m => m.DailyScrumMatrixPage)
  },

  // ── DSM User-Day: Read (Detail-2a) — TODO: noch nicht gebaut ─────────────
  // Wenn diese Route gebaut wird:
  //   capability: DAILY_VIEW
  // {
  //   path: 'project/:pslot/daily-scrum-matrix/sprint/:sp_id/day/:cell_date/user/:uuid',
  //   canActivate: [calStatusGuard, projectAccessGuard],
  //   loadComponent: () =>
  //     import('./pages/users-day/users-day.page').then(m => m.UsersDayPage)
  // },

  // ── DSM User-Day: Edit (Detail-2b): braucht DAILY_OWN_EDIT ───────────────
  {
    path: 'project/:pslot/daily-scrum-matrix/sprint/:sp_id/day/:cell_date/user/:uuid/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für DAILY_OWN_EDIT)
    loadComponent: () =>
      import('./pages/users-day-edit/users-day-edit.page')
        .then(m => m.UsersDayEditPage)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // LEGACY SPRINT ROUTES (Backward Compatibility)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Legacy Sprint Settings (Sprint Creator alt): braucht SPRINT_CREATE ──
  {
    path: 'project/:pslot/sprintsettingsset',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für SPRINT_CREATE)
    loadComponent: () =>
      import('./pages/sprint-creator/sprint-creator-page.component')
        .then(m => m.SprintCreatorPageComponent)
  },

  /*
   * Auskommentiert seit v2.x — Sprint-Update läuft jetzt über
   * /project/:pslot/sprint/:sp_id/edit (canonical S/SEEP).
   *
   * {
   *   path: 'project/:pslot/sprintsettingsmod/sprintid/:sp_id',
   *   canActivate: [calStatusGuard, projectAccessGuard],
   *   loadComponent: () =>
   *     import('./pages/sprint-update/sprint-update-page.component')
   *       .then(m => m.SprintUpdatePageComponent)
   * },
   */

  // ─────────────────────────────────────────────────────────────────────────
  // MILESTONES (Legacy Creator)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Legacy Milestones-Creator: braucht MILESTONE_CREATE ──────────────────
  {
    path: 'project/:pslot/milestones/create',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für MILESTONE_CREATE)
    loadComponent: () =>
      import('./pages/milestone-creator/milestone-creator-page.component')
        .then(m => m.MilestoneCreatorPageComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // /no-license (Public — kein Guard)
  // ─────────────────────────────────────────────────────────────────────────
  // Wenn ein User keine CAL hat, wird er hierher redirected.
  // MUSS public bleiben, sonst kann er hier nicht hinkommen → CAL-Falle.
  {
    path: 'no-license',
    loadComponent: () =>
      import('./pages/no-license-page/no-license-page.component')
        .then(m => m.NoLicensePageComponent)
    // ✅ NO GUARD - User MUSS hier hinkönnen wenn er keine CAL hat
  },

  // ─────────────────────────────────────────────────────────────────────────
  // /self-assign (NUR Tryout-Instanz, public)
  // ─────────────────────────────────────────────────────────────────────────
  {
    path: 'self-assign',
    loadComponent: () =>
      import('./pages/self-assign-page/self-assign-page.component')
        .then(m => m.SelfAssignPageComponent)
    // ✅ NO GUARD - Self-Service auf Tryout, soll auch ohne CAL funktionieren
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ACCESS & ADMIN
  // Capability-Map:
  //   /request-access     → keine Capability nötig (Pending User)
  //   /manage-access      → PROJECT_ACCESS_MANAGE (Project Admin+)
  //   /project-create     → PROJECT_CREATE (jeder mit Org-User-Permission)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Request Project Access: KEINE Capability nötig ───────────────────────
  // Pending User (perm=0) müssen hier hinkönnen um Zugriff zu beantragen.
  // page-access.config.ts setzt requires: [] für diese Route.
  {
    path: 'project/:pslot/request-access',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (capability check ergibt aber immer "ok")
    loadComponent: () =>
      import('./pages/project-access-request/project-access-request-page.component')
        .then(m => m.ProjectAccessRequestPageComponent)
  },

  // ── Manage Access: braucht PROJECT_ACCESS_MANAGE ─────────────────────────
  {
    path: 'project/:pslot/manage-access',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für PROJECT_ACCESS_MANAGE)
    loadComponent: () =>
      import('./pages/project-access-manage-page/project-access-manage-page.component')
        .then(m => m.ProjectAccessManagePageComponent)
  },

  // ── Project Create: braucht PROJECT_CREATE ───────────────────────────────
  {
    path: 'project-create',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für PROJECT_CREATE)
    loadComponent: () =>
      import('./pages/project-create/project-create-page.component')
        .then(m => m.ProjectCreatePageComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // APP MANAGEMENT (Light Theme)
  //
  // Wichtig: für /app-management selbst wird KEINE CAL gebraucht — Admins
  // sollen das Portal auch nutzen können wenn ihre eigene CAL fehlt.
  // Die calStatusGuard-Logik beinhaltet einen Super-Admin-Bypass für
  // orgPerm >= 7. User mit orgPerm 5/6 brauchen aber eine CAL.
  //
  // Capability-Map:
  //   /app-management/login           → public
  //   /app-management                 → APP_MANAGEMENT_ACCESS (org-scope)
  //   /app-management/bulk-user-...   → USER_PROVISION (org-scope)
  // ─────────────────────────────────────────────────────────────────────────

  // ── App-Management Login: public ─────────────────────────────────────────
  {
    path: 'app-management/login',
    loadComponent: () =>
      import('./pages/app-management/app-management-login.component')
        .then(m => m.AppManagementLoginComponent)
    // ✅ NO GUARD - Login page muss immer erreichbar sein
  },

  // ── CAL Management Page: braucht APP_MANAGEMENT_ACCESS ───────────────────
  {
    path: 'app-management',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für APP_MANAGEMENT_ACCESS, org-scope)
    //   Super-Admin (orgPerm 7) bypasst auch den projectAccessGuard via Service-Logik.
    loadComponent: () =>
      import('./pages/app-management/cal-management-page.component')
        .then(m => m.CalManagementPageComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ⚠️ BUGFIX in v3.0.0: bulk-user-provisioning hatte bisher KEINE Guards!
  // ─────────────────────────────────────────────────────────────────────────
  // Bisher (v2.0.0):  KEIN Guard → jeder konnte die Page-Komponente laden
  // Jetzt (v3.0.0):   [calStatusGuard, projectAccessGuard] → erst Session+CAL,
  //                   dann USER_PROVISION-Capability prüfen
  //
  // Die Backend-API war zwar geschützt (requireOrgPermission(5)), aber die
  // Page hätte sich für Unauthentifizierte trotzdem laden lassen — das wäre
  // ein Information-Disclosure-Risiko gewesen (UI verrät Admin-Funktionen).
  //
  // Capability für diese Route in page-access.config.ts: USER_PROVISION
  // ─────────────────────────────────────────────────────────────────────────
  {
    path: 'app-management/bulk-user-provisioning',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0 BUGFIX: vorher KEIN Guard, jetzt beide Layer aktiv
    loadComponent: () =>
      import('./pages/app-management/bulk-user-provisioning-page.component')
        .then(m => m.BulkUserProvisioningPageComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PROFILE MANAGEMENT
  // Capability-Map:
  //   /my-profile     → OWN_PROFILE_VIEW (jeder eingeloggte User)
  //   /rt-assessment  → RT_ASSESSMENT_OWN (jeder eingeloggte User)
  // ─────────────────────────────────────────────────────────────────────────

  // ── ProjFiles Management System: /project/:pslot/projfiles ───────────────
  //   Alle Dateien eines Projekts, filterbar, mit aufklappbaren Versionen.
  //   Item #121 / Task 600, 2026-09-11.
  //
  //   Guards wie bei den uebrigen Projektseiten. Was der Betrachter zu sehen
  //   bekommt, entscheidet ohnehin das Backend: jede Route unter /api/projfiles
  //   verlangt requireProjectAccess — ein Guard im Frontend ist Bequemlichkeit,
  //   keine Kontrolle.
  {
    path: 'project/:pslot/projfiles',
    canActivate: [calStatusGuard, projectAccessGuard],
    loadComponent: () =>
      import('./pages/projfiles/projfiles-page.component')
        .then(m => m.ProjFilesPageComponent)
  },

  // ── Fremdes Profil: /profile/:uuid ───────────────────────────────────────
  //   Ziel des Klicks auf Avatar oder Namen an einem Diskussionsbeitrag
  //   (Item #120, 2026-09-04). Read-only.
  //
  //   Bewusst NUR calStatusGuard, KEIN projectAccessGuard: die Seite gehoert zu
  //   keinem pslot. Ein Beitrag in Projekt A kann von jemandem stammen, den man
  //   aus Projekt B kennt — ein projektgebundener Guard wuerde den Verweis genau
  //   dann ins Leere laufen lassen, wenn er am meisten hilft.
  //   Was der Betrachter zu sehen bekommt, entscheidet ohnehin das Backend:
  //   GET /api/persprofile/public/:uuid filtert nach profile_visibility und
  //   verlangt eine gueltige Anmeldung. Der Guard hier ist die Anmeldepflicht,
  //   nicht die Datenfreigabe.
  {
    path: 'profile/:uuid',
    canActivate: [calStatusGuard],
    loadComponent: () =>
      import('./pages/user-profile/user-profile-page.component')
        .then(m => m.UserProfilePageComponent)
  },

  // ── My Profile: braucht OWN_PROFILE_VIEW ─────────────────────────────────
  {
    path: 'my-profile',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für OWN_PROFILE_VIEW)
    loadComponent: () =>
      import('./pages/manage-myprofile/manage-myprofile.page')
        .then(m => m.ManageMyprofilePage)
  },

  // ── RT Assessment (org-level): braucht RT_ASSESSMENT_OWN ─────────────────
  {
    path: 'rt-assessment',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für RT_ASSESSMENT_OWN)
    loadComponent: () =>
      import('./pages/riemann-thomann-test/riemann-thomann-test.page')
        .then(m => m.RiemannThomannTestPage)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PERSONA-MAKER
  // Capability-Map:
  //   /persona/new/edit       → PERSONA_MANAGE (PO / SM / Manager)
  //   /persona/:id/edit       → PERSONA_MANAGE (PO / SM / Manager)
  //   /persona/:id            → PERSONA_VIEW   (jeder Member)
  //   /personas               → PERSONA_VIEW   (jeder Member)
  // ─────────────────────────────────────────────────────────────────────────

  // ── Persona-Creator: braucht PERSONA_MANAGE ──────────────────────────────
  {
    path: 'project/:pslot/wbs',
    canActivate: [calStatusGuard, projectAccessGuard],
    loadComponent: () =>
      import('./pages/wbs-maker/wbs-maker-page.component')
        .then(m => m.WbsMakerPageComponent)
  },

  // ── Persona-Maker: braucht PERSONA_MANAGE ───────────────────────────────
  {
    path: 'project/:pslot/persona/new/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für PERSONA_MANAGE)
    loadComponent: () =>
      import('./pages/persona-maker/persona-maker-page.component')
        .then(m => m.PersonaMakerPageComponent)
  },

  // ── Persona-Editor: braucht PERSONA_MANAGE ───────────────────────────────
  {
    path: 'project/:pslot/persona/:persona_id/edit',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für PERSONA_MANAGE)
    loadComponent: () =>
      import('./pages/persona-maker/persona-maker-page.component')
        .then(m => m.PersonaMakerPageComponent)
  },

  // ── Persona-View (Detail): braucht PERSONA_VIEW ──────────────────────────
  {
    path: 'project/:pslot/persona/:persona_id',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für PERSONA_VIEW)
    loadComponent: () =>
      import('./pages/persona-view/persona-view-page.component')
        .then(m => m.PersonaViewPageComponent)
  },

  // ── Personas List: braucht PERSONA_VIEW ──────────────────────────────────
  {
    path: 'project/:pslot/personas',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für PERSONA_VIEW)
    loadComponent: () =>
      import('./pages/personas-list/personas-list-page.component')
        .then(m => m.PersonasListPageComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // RIEMANN-THOMANN TEST (Project-scope)
  // Capability: RT_ASSESSMENT_OWN
  // ─────────────────────────────────────────────────────────────────────────
  {
    path: 'project/:pslot/rt-assessment',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für RT_ASSESSMENT_OWN)
    loadComponent: () =>
      import('./pages/riemann-thomann-test/riemann-thomann-test.page')
        .then(m => m.RiemannThomannTestPage)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MISC / TOOLS
  // ─────────────────────────────────────────────────────────────────────────

  // ── Third-Party Config: braucht THIRD_PARTY_CONFIG ───────────────────────
  {
    path: 'thirdparty-config',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (für THIRD_PARTY_CONFIG)
    loadComponent: () =>
      import('./pages/thirdparty-config/thirdparty-config-page.component')
        .then(m => m.ThirdpartyConfigPageComponent)
  },

  // ── Time-View: keine spezielle Capability ────────────────────────────────
  {
    path: 'time',
    canActivate: [calStatusGuard, projectAccessGuard],
    // ↑ v3.0.0: + projectAccessGuard (page-access.config.ts: requires: [])
    loadComponent: () =>
      import('./pages/time/time-view.component')
        .then(m => m.TimeViewComponent)
  },

  // ─────────────────────────────────────────────────────────────────────────
  // /info-target-mapper — bewusst OHNE Guards
  // ─────────────────────────────────────────────────────────────────────────
  // Begründung (kommt aus v2.0.0, hier dokumentiert für v3.0.0):
  //   Die Page wird in Auth-Error-Flows als Zwischenseite genutzt — sie wird
  //   ggf. aufgerufen BEVOR eine gültige Session existiert. Mit Guard wäre
  //   das ein Endlos-Redirect-Loop.
  //
  //   Wenn du in v3.0.0 darüber nachdenkst Guards hinzuzufügen: NICHT TUN.
  //   Die Page selbst hat keine sensiblen Daten — sie ist ein Redirect-
  //   Coordinator.
  // ─────────────────────────────────────────────────────────────────────────
  {
    path: 'info-target-mapper',
    // canActivate: [calStatusGuard, projectAccessGuard],
    //   ↑ ABSICHTLICH AUSKOMMENTIERT (siehe Erklärung oben)
    //
    // Historie:
    // - v2.0.0 < 7.März 2026 hatte mal calStatusGuard drauf
    // - Wurde wieder rausgenommen wegen Issues bei OAuth-Provider-Wechsel
    // - v3.0.0 lässt das so wie es war: KEIN Guard

    loadComponent: () =>
      import('./pages/info-target-mapper/info-target-mapper.component')
        .then(m => m.InfoTargetMapperComponent)
  }
];
